// SOA Tracking — the operator's live view of every soa_v2_records
// row in their book. Matches the screenshot Harrison shared: status
// table with count badges per state + filter chips + +Send SOA action.
//
// Reads /api/soa-v2/list; writes via /api/soa-v2/send +
// /api/soa-v2/countersign.

import React from 'react';
import { readPersistedJwtDirect } from '../lib/authedFetch';
import { ShieldCheck, FilePlus, AlertCircle, CheckCircle2, ExternalLink, RefreshCcw, X, Pause, Play } from 'lucide-react';
import { supabase } from '../lib/supabase';

const FORM_TYPES = [
  { key: 'all',          label: 'all forms'    },
  { key: 'medicare_soa', label: 'Medicare SOA' },
  { key: 'aca_consent',  label: 'ACA consent'  },
];

async function apiFetch(path, init = {}) {
  const jwt = readPersistedJwtDirect();
  const res = await fetch(path, {
    ...init,
    headers: { 'content-type': 'application/json', ...(jwt ? { Authorization: `Bearer ${jwt}` } : {}), ...(init.headers || {}) },
  });
  const body = await res.json().catch(() => ({}));
  if (!res.ok) {
    const e = new Error(body?.error || `HTTP ${res.status}`);
    e.status = res.status; e.body = body;
    throw e;
  }
  return body;
}

const PRODUCT_CATALOG = [
  { id: 'ma_ppo',        label: 'MA PPO' },
  { id: 'ma_hmo',        label: 'MA HMO' },
  { id: 'pdp',           label: 'PDP (Part D)' },
  { id: 'dental_vision', label: 'Dental / Vision' },
  { id: 'medigap',       label: 'Medigap' },
];

const FILTERS = [
  { key: 'all',            label: 'all',            color: 'slate'  },
  { key: 'pending_client', label: 'pending client', color: 'amber'  },
  { key: 'pending_agent',  label: 'pending agent',  color: 'sky'    },
  { key: 'completed',      label: 'completed',      color: 'emerald'},
  { key: 'expired',        label: 'expired',        color: 'slate'  },
  { key: 'revoked',        label: 'revoked',        color: 'rose'   },
];

function fmtDateShort(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString(undefined, {
      month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    });
  } catch { return iso; }
}

function statusChip(status, compliance) {
  const meta = {
    pending_client: { label: 'Pending client', bg: 'bg-amber-100',  text: 'text-amber-800',  border: 'border-amber-200'  },
    pending_agent:  { label: 'Pending agent',  bg: 'bg-sky-100',    text: 'text-sky-800',    border: 'border-sky-200'    },
    completed:      { label: 'Completed',      bg: 'bg-emerald-100',text: 'text-emerald-800',border: 'border-emerald-200'},
    expired:        { label: 'Expired',        bg: 'bg-slate-100',  text: 'text-slate-700',  border: 'border-slate-200'  },
    revoked:        { label: 'Revoked',        bg: 'bg-rose-100',   text: 'text-rose-800',   border: 'border-rose-200'   },
  }[status] || { label: status, bg: 'bg-slate-100', text: 'text-slate-700', border: 'border-slate-200' };
  return (
    <span className={`text-[10px] font-bold uppercase tracking-wider rounded-full px-1.5 py-0.5 border inline-flex items-center gap-1 ${meta.bg} ${meta.text} ${meta.border}`}>
      {meta.label}
      {compliance === 'same_day_exception' && <span title="Same-day exception documented">⚡</span>}
      {compliance === 'non_compliant'      && <span title="Non-compliant — appointment time was unknown at sign">⚠</span>}
    </span>
  );
}

export default function SOATrackingView({ currentUser, showToast = () => {} }) {
  const [data, setData]       = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [filter, setFilter]   = React.useState('all');
  const [formFilter, setFormFilter] = React.useState('all');
  const [sendOpen, setSendOpen] = React.useState(false);
  const [sendPrefill, setSendPrefill] = React.useState(null);
  const [countersigning, setCountersigning] = React.useState(null);
  const [resendingId, setResendingId] = React.useState(null);

  // Auto-open the Send modal when ClientFormsPanel navigates here with
  // prefill. Window-scoped handoff because we don't want the prefill
  // in URL state.
  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    const pf = window.__soaSendPrefill;
    if (pf) {
      setSendPrefill(pf);
      setSendOpen(true);
      try { delete window.__soaSendPrefill; } catch {}
    }
  }, []);

  const load = React.useCallback(async () => {
    if (!currentUser?.id) return;
    setLoading(true);
    try {
      const r = await apiFetch(`/api/soa-v2/list?status=${encodeURIComponent(filter)}&form_type=${encodeURIComponent(formFilter)}`);
      setData(r);
    } catch (err) {
      showToast(`Load failed: ${err.message}`, 'error');
    } finally { setLoading(false); }
  }, [currentUser?.id, filter, formFilter, showToast]);

  // Operator override: toggle the chase pause flag on a record. Used
  // for both "Stop chasing this client" and "Resume chasing." The
  // CONSENT cron skips paused records entirely.
  const toggleChasePause = async (rec, next) => {
    try {
      const { error } = await supabase.from('soa_v2_records')
        .update({ consent_chase_paused: next })
        .eq('id', rec.id);
      if (error) { showToast(`Update failed: ${error.message}`, 'error'); return; }
      showToast(next ? 'Chase paused for this record.' : 'Chase resumed.', 'success');
      load();
    } catch (err) {
      showToast(`Update failed: ${err.message}`, 'error');
    }
  };

  // Manually re-send the signing link to a client who still needs to
  // sign. Mints a fresh token and dispatches BOTH text + email (whichever
  // the record has), gated server-side. Complements the CONSENT auto-chase
  // with an operator-initiated push.
  const resendSoa = async (rec) => {
    setResendingId(rec.id);
    try {
      const r = await apiFetch('/api/soa-v2/resend', { method: 'POST', body: JSON.stringify({ soa_id: rec.id }) });
      const d = r?.dispatched || {};
      const sent = [];
      if (d.sms?.ok)   sent.push(d.sms.dry_run   ? 'text (test mode)'  : 'text');
      if (d.email?.ok) sent.push(d.email.dry_run ? 'email (test mode)' : 'email');
      if (sent.length) showToast(`Signing link re-sent by ${sent.join(' + ')}.`, 'success');
      else {
        const why = d.sms?.error || d.email?.error;
        showToast(why ? `Couldn't send: ${why}` : 'No phone or email on file for this client.', 'error');
      }
      load();
    } catch (err) {
      showToast(`Resend failed: ${err.message}`, 'error');
    } finally { setResendingId(null); }
  };

  React.useEffect(() => { load(); }, [load]);

  // Cert URL strategy: mint a signed link via /api/soa-v2/cert-link
  // (operator JWT-authed), then open the returned URL in a new tab.
  // The URL works without the operator's session — the HMAC in the
  // query string is the authorization. Three wins over the prior
  // blob-URL approach:
  //   1. Survives stale browser sessions / cached JS
  //   2. Operator can copy the URL + send it to a CMS auditor
  //   3. Bookmarkable until the TTL expires (default 60 min)
  const openCertificate = async (soaId) => {
    try {
      const jwt = readPersistedJwtDirect();
      if (!jwt) { showToast('Not signed in — refresh + sign back in.', 'error'); return; }
      const linkRes = await fetch(`/api/soa-v2/cert-link?soa_id=${encodeURIComponent(soaId)}`, {
        headers: { Authorization: `Bearer ${jwt}` },
      });
      const body = await linkRes.json().catch(() => ({}));
      if (!linkRes.ok || !body?.url) {
        showToast(`Cert link failed: ${body?.error || linkRes.status}`, 'error');
        return;
      }
      const win = window.open(body.url, '_blank', 'noopener');
      if (!win) {
        showToast('Browser blocked the cert window. Allow pop-ups, then click Cert again.', 'error');
      }
    } catch (err) {
      showToast(`Cert load failed: ${err.message}`, 'error');
    }
  };

  const summary = data?.summary || {};
  const records = data?.records || [];

  return (
    <div className="max-w-none mx-auto px-4 py-4 space-y-4">
      {/* Hero — title + counts + Send form */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="flex items-baseline justify-between gap-3 flex-wrap">
          <div>
            <h2 className="font-bold text-slate-900 inline-flex items-center gap-2">
              <ShieldCheck size={16} className="text-sky-700"/>
              Compliance forms
            </h2>
            <div className="text-xs text-slate-500 mt-0.5">
              Medicare SOA (MED-136) · ACA Marketplace consent · Audit-defensible · 10-year retention
            </div>
            <div className="text-xs text-slate-600 mt-2 tabular-nums">
              {summary.total || 0} records · {summary.pending_client || 0} awaiting client · {summary.pending_agent || 0} awaiting countersign · {summary.completed || 0} completed
            </div>
          </div>
          <button
            onClick={() => setSendOpen(true)}
            className="bg-slate-900 hover:bg-black text-white text-sm font-bold px-4 py-2 rounded-lg inline-flex items-center gap-1.5"
          >
            <FilePlus size={14}/> Send form
          </button>
        </div>
      </div>

      {/* Compliance reminder banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-xs text-amber-900 leading-relaxed flex items-start gap-2">
        <AlertCircle size={14} className="text-amber-700 mt-0.5 shrink-0"/>
        <div>
          <strong>Compliance reminder:</strong> Medicare enrollment CANNOT be marked complete without an SOA signed ≥48 hours before the appointment (or a documented same-day exception), a recorded call, and current AHIP + carrier certs. CMS audit retention: 10 years.
        </div>
      </div>

      {/* Form-type filter chips */}
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Form type:</span>
        {FORM_TYPES.map(f => {
          const active = formFilter === f.key;
          return (
            <button
              key={f.key}
              onClick={() => setFormFilter(f.key)}
              className={`text-xs font-bold uppercase tracking-wider px-2.5 py-1 rounded-full border transition-colors ${
                active
                  ? 'bg-sky-100 text-sky-900 border-sky-300'
                  : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
              }`}
            >
              {f.label}
            </button>
          );
        })}
      </div>

      {/* Status filter chips */}
      <div className="flex items-center gap-2 flex-wrap">
        {FILTERS.map(f => {
          const count = f.key === 'all' ? (summary.total || 0) : (summary[f.key] || 0);
          const active = filter === f.key;
          return (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-full border transition-colors ${
                active
                  ? 'bg-slate-900 text-white border-slate-900'
                  : 'bg-white text-slate-700 border-slate-200 hover:bg-slate-50'
              }`}
            >
              {f.label} · {count}
            </button>
          );
        })}
        <button onClick={load} className="ml-auto text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1">
          <RefreshCcw size={11}/> Refresh
        </button>
      </div>

      {/* Records table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="px-5 py-10 text-center text-sm text-slate-500">Loading…</div>
        ) : records.length === 0 ? (
          <div className="px-5 py-10 text-center text-sm text-slate-500">
            No SOAs match. Click <strong>+ Send SOA</strong> to start.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-[10px] uppercase tracking-wider text-slate-500 font-bold border-b border-slate-100">
              <tr>
                <th className="text-left px-4 py-2.5">Beneficiary</th>
                <th className="text-left px-4 py-2.5">Products</th>
                <th className="text-left px-4 py-2.5">Status</th>
                <th className="text-left px-4 py-2.5">Client signed</th>
                <th className="text-left px-4 py-2.5">Agent signed</th>
                <th className="text-left px-4 py-2.5">Appointment</th>
                <th className="text-right px-4 py-2.5"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {records.map(r => {
                const cSig = r.signatures?.client;
                const aSig = r.signatures?.agent;
                const isAca = r.form_type === 'aca_consent';
                const productsLabel = isAca
                  ? 'Marketplace + eligibility attestation'
                  : (r.product_scope || []).join(', ') || '—';
                const isPaused = r.consent_chase_paused === true;
                const touchCount = r.chase_touch_count || 0;
                return (
                  <tr key={r.id} className="hover:bg-slate-50">
                    <td className="px-4 py-2.5">
                      <div className="font-semibold text-slate-900">{r.beneficiary_name || '—'}</div>
                      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-400 mt-0.5">
                        {isAca ? 'ACA consent' : 'Medicare SOA'}
                      </div>
                    </td>
                    <td className="px-4 py-2.5 text-slate-600 text-xs">{productsLabel}</td>
                    <td className="px-4 py-2.5">
                      {statusChip(r.status, r.compliance_flag)}
                      {r.status === 'pending_client' && touchCount > 0 && (
                        <div className="text-[10px] text-slate-500 mt-1">
                          {isPaused ? 'Chase paused' : `${touchCount} touch${touchCount === 1 ? '' : 'es'} sent`}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-2.5 text-xs text-slate-500 tabular-nums">{fmtDateShort(cSig?.signed_at)}</td>
                    <td className="px-4 py-2.5 text-xs text-slate-500 tabular-nums">{isAca ? 'n/a' : fmtDateShort(aSig?.signed_at)}</td>
                    <td className="px-4 py-2.5 text-xs text-slate-500 tabular-nums">{fmtDateShort(r.appointment_at)}</td>
                    <td className="px-4 py-2.5 text-right">
                      <div className="flex items-center gap-1 justify-end">
                        {r.status === 'pending_client' && (
                          <button
                            onClick={() => resendSoa(r)}
                            disabled={resendingId === r.id}
                            className="text-xs font-bold bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white rounded-md px-2.5 py-1 inline-flex items-center gap-1"
                            title="Re-send the signing link now by text + email"
                          >
                            <RefreshCcw size={11}/> {resendingId === r.id ? 'Sending…' : 'Resend'}
                          </button>
                        )}
                        {r.status === 'pending_client' && (
                          <button
                            onClick={() => toggleChasePause(r, !isPaused)}
                            className={`text-xs font-bold rounded-md px-2 py-1 inline-flex items-center gap-1 border ${
                              isPaused
                                ? 'bg-slate-100 text-slate-700 border-slate-300 hover:bg-slate-200'
                                : 'bg-white text-amber-700 border-amber-300 hover:bg-amber-50'
                            }`}
                            title={isPaused ? 'Resume CONSENT auto-follow-up' : 'Stop CONSENT auto-follow-up for this record'}
                          >
                            {isPaused ? <><Play size={10}/> Resume</> : <><Pause size={10}/> Stop chasing</>}
                          </button>
                        )}
                        {r.status === 'pending_agent' && !isAca && (
                          <button
                            onClick={() => setCountersigning(r)}
                            className="text-xs font-bold bg-emerald-600 hover:bg-emerald-700 text-white rounded-md px-2.5 py-1 inline-flex items-center gap-1"
                          >
                            <CheckCircle2 size={11}/> Countersign
                          </button>
                        )}
                        {r.status === 'completed' && r.id && (
                          <button
                            onClick={() => openCertificate(r.id)}
                            className="text-xs font-bold text-sky-700 hover:text-sky-900 inline-flex items-center gap-1"
                            title="Open audit certificate in new tab"
                          >
                            <ExternalLink size={11}/> Cert
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {sendOpen && (
        <SendSoaModal
          prefill={sendPrefill}
          onClose={() => { setSendOpen(false); setSendPrefill(null); }}
          onSent={() => { setSendOpen(false); setSendPrefill(null); load(); }}
          showToast={showToast}
        />
      )}
      {countersigning && (
        <CountersignModal
          record={countersigning}
          currentUser={currentUser}
          onClose={() => setCountersigning(null)}
          onSigned={() => { setCountersigning(null); load(); }}
          showToast={showToast}
        />
      )}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Send modal — beneficiary info + product checkboxes + appointment +
// dispatch channels.
// ──────────────────────────────────────────────────────────────────
function SendSoaModal({ prefill = null, onClose, onSent, showToast }) {
  const [formType, setFormType] = React.useState(prefill?.form_type || 'medicare_soa');
  const [name,  setName]  = React.useState(prefill?.beneficiary_name || '');
  const [phone, setPhone] = React.useState(prefill?.beneficiary_phone || '');
  const [email, setEmail] = React.useState(prefill?.beneficiary_email || '');
  const beneficiaryId = prefill?.beneficiary_id || null;
  const [products, setProducts] = React.useState([]);
  const [appointmentAt, setAppointmentAt] = React.useState(() => {
    const d = new Date(Date.now() + 3 * 86_400_000);
    d.setHours(13, 0, 0, 0);
    const pad = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  });
  const [busy, setBusy] = React.useState(false);
  const isAca = formType === 'aca_consent';

  const toggle = (id) => setProducts(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  const send = async () => {
    if (!name.trim()) { showToast('Name required.', 'error'); return; }
    if (!phone && !email) { showToast('Phone or email required.', 'error'); return; }
    if (!isAca && products.length === 0) { showToast('Pick at least one product.', 'error'); return; }
    setBusy(true);
    try {
      const r = await apiFetch('/api/soa-v2/send', {
        method: 'POST',
        body: JSON.stringify({
          form_type:         formType,
          beneficiary_id:    beneficiaryId,
          beneficiary_name:  name.trim(),
          beneficiary_phone: phone.trim() || null,
          beneficiary_email: email.trim() || null,
          product_scope:     isAca ? [] : products,
          appointment_at:    isAca ? null : (appointmentAt ? new Date(appointmentAt).toISOString() : null),
        }),
      });
      const dispatched = [];
      if (r.dispatched?.sms?.ok)    dispatched.push('SMS sent');
      if (r.dispatched?.email?.ok)  dispatched.push('email sent');
      showToast(`SOA sent · ${dispatched.join(' + ') || 'no channel landed'}`, 'success');
      onSent();
    } catch (err) {
      showToast(`Send failed: ${err.message}`, 'error');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-lg rounded-xl shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-bold">Send compliance form</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18}/></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500 block mb-1">Form</span>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setFormType('medicare_soa')}
                className={`text-left rounded-lg border p-2 ${formType === 'medicare_soa' ? 'border-sky-500 bg-sky-50' : 'border-slate-200 bg-white hover:bg-slate-50'}`}
              >
                <div className="text-xs font-bold text-slate-900">Medicare SOA</div>
                <div className="text-[10px] text-slate-500 mt-0.5">MED-136 · Two-signature</div>
              </button>
              <button
                type="button"
                onClick={() => setFormType('aca_consent')}
                className={`text-left rounded-lg border p-2 ${formType === 'aca_consent' ? 'border-sky-500 bg-sky-50' : 'border-slate-200 bg-white hover:bg-slate-50'}`}
              >
                <div className="text-xs font-bold text-slate-900">ACA consent</div>
                <div className="text-[10px] text-slate-500 mt-0.5">Marketplace · Single-signature</div>
              </button>
            </div>
          </div>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Beneficiary name</span>
            <input value={name} onChange={e => setName(e.target.value)} className="w-full mt-1 border border-slate-300 rounded px-2 py-1.5 text-sm" placeholder="First Last"/>
          </label>
          <div className="grid grid-cols-2 gap-2">
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Phone (SMS)</span>
              <input value={phone} onChange={e => setPhone(e.target.value)} className="w-full mt-1 border border-slate-300 rounded px-2 py-1.5 text-sm" placeholder="(555) 123-4567"/>
            </label>
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Email</span>
              <input value={email} onChange={e => setEmail(e.target.value)} className="w-full mt-1 border border-slate-300 rounded px-2 py-1.5 text-sm" placeholder="you@example.com"/>
            </label>
          </div>
          {!isAca && (
            <div>
              <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Products to discuss</div>
              <div className="grid grid-cols-2 gap-1.5">
                {PRODUCT_CATALOG.map(p => (
                  <label key={p.id} className="flex items-center gap-2 text-sm cursor-pointer p-1.5 rounded hover:bg-slate-50">
                    <input
                      type="checkbox"
                      checked={products.includes(p.id)}
                      onChange={() => toggle(p.id)}
                    />
                    {p.label}
                  </label>
                ))}
              </div>
            </div>
          )}
          {!isAca && (
            <label className="block">
              <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Appointment date / time</span>
              <input
                type="datetime-local"
                value={appointmentAt}
                onChange={e => setAppointmentAt(e.target.value)}
                className="w-full mt-1 border border-slate-300 rounded px-2 py-1.5 text-sm"
              />
              <span className="text-[10px] text-slate-500 mt-0.5 block">CMS requires SOA at least 48 hours before. Inside-window sends prompt the client for a documented exception reason.</span>
            </label>
          )}
          {isAca && (
            <div className="bg-amber-50 border border-amber-200 rounded p-3 text-xs text-amber-900 leading-relaxed">
              <strong>ACA consent</strong> captures both consumer consent to assist + eligibility-application-reviewed-for-accuracy attestation. Single signature (consumer only) — no agent countersign. Required attestations are surfaced on the signing page.
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-end gap-2">
          <button onClick={onClose} className="text-sm text-slate-600 px-3 py-1.5">Cancel</button>
          <button
            onClick={send}
            disabled={busy}
            className="bg-slate-900 hover:bg-black text-white text-sm font-bold px-4 py-1.5 rounded-lg disabled:opacity-50"
          >
            {busy ? 'Sending…' : 'Send signing link'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────
// Countersign modal — agent's signature on a pending_agent record.
// ──────────────────────────────────────────────────────────────────
function CountersignModal({ record, currentUser, onClose, onSigned, showToast }) {
  const [name, setName] = React.useState(currentUser?.email?.split('@')[0] || '');
  const [intent, setIntent] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const canvasRef = React.useRef(null);

  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const dpr = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);
    ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    ctx.strokeStyle = '#0f172a'; ctx.lineWidth = 2.5;
    let drawing = false; let last = null;
    const pos = e => {
      const r = canvas.getBoundingClientRect();
      return { x: e.clientX - r.left, y: e.clientY - r.top };
    };
    const onDown = e => { drawing = true; last = pos(e); canvas.setPointerCapture(e.pointerId); };
    const onMove = e => {
      if (!drawing || !last) return;
      const p = pos(e);
      ctx.beginPath(); ctx.moveTo(last.x, last.y); ctx.lineTo(p.x, p.y); ctx.stroke();
      last = p;
    };
    const onUp = () => { drawing = false; last = null; };
    canvas.addEventListener('pointerdown', onDown);
    canvas.addEventListener('pointermove', onMove);
    canvas.addEventListener('pointerup', onUp);
    canvas.addEventListener('pointercancel', onUp);
    return () => {
      canvas.removeEventListener('pointerdown', onDown);
      canvas.removeEventListener('pointermove', onMove);
      canvas.removeEventListener('pointerup', onUp);
      canvas.removeEventListener('pointercancel', onUp);
    };
  }, []);

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const submit = async () => {
    if (!name.trim() || name.trim().length < 2) { showToast('Type your name.', 'error'); return; }
    if (!intent) { showToast('Confirm intent to sign.', 'error'); return; }
    const canvas = canvasRef.current;
    const dataUrl = canvas ? canvas.toDataURL('image/png') : '';
    if (!dataUrl || dataUrl.length < 5000) { showToast('Please sign in the box.', 'error'); return; }
    setBusy(true);
    try {
      await apiFetch('/api/soa-v2/countersign', {
        method: 'POST',
        body: JSON.stringify({
          soa_id:             record.id,
          signer_name:        name.trim(),
          intent_confirmed:   true,
          signature_data_url: dataUrl,
        }),
      });
      showToast('Countersigned. Record locked + certificate ready.', 'success');
      onSigned();
    } catch (err) {
      showToast(`Countersign failed: ${err.message}`, 'error');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white w-full max-w-md rounded-xl shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between">
          <h3 className="font-bold">Countersign — {record.beneficiary_name}</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18}/></button>
        </div>
        <div className="p-5 space-y-3">
          <div>
            <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500 block mb-1">Your signature</span>
            <canvas
              ref={canvasRef}
              className="w-full h-32 border-2 border-dashed border-slate-300 rounded bg-white touch-none"
              aria-label="Sign with finger or mouse"
            />
            <button type="button" onClick={clearCanvas} className="text-xs text-slate-500 hover:text-slate-900 mt-1">Clear</button>
          </div>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Your name</span>
            <input value={name} onChange={e => setName(e.target.value)} className="w-full mt-1 border border-slate-300 rounded px-2 py-1.5 text-sm"/>
          </label>
          <label className="flex items-start gap-2 text-xs cursor-pointer">
            <input type="checkbox" checked={intent} onChange={e => setIntent(e.target.checked)} className="mt-0.5"/>
            <span>I am the licensed agent of record. I agree this is my electronic signature countersigning the beneficiary's SOA.</span>
          </label>
        </div>
        <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-end gap-2">
          <button onClick={onClose} className="text-sm text-slate-600 px-3 py-1.5">Cancel</button>
          <button
            onClick={submit}
            disabled={busy}
            className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-bold px-4 py-1.5 rounded-lg disabled:opacity-50 inline-flex items-center gap-1"
          >
            {busy ? 'Signing…' : (<><CheckCircle2 size={12}/> Sign + complete</>)}
          </button>
        </div>
      </div>
    </div>
  );
}
